/**
 * Runtime configuration, fetched from /config.json before the app renders.
 *
 * Deliberately not build-time env vars: one build artifact deploys to every
 * environment, and ops already understands file-on-disk configuration because
 * MINT works exactly that way. In production the file lives outside the
 * released directory so a deploy never overwrites it.
 */

export type ErpMode = 'DUAL' | 'ORACLE_ONLY'

export interface AppConfig {
  apiBaseUrl: string
  /** Full URL of the monitoring app. Opened in a new tab; see LandingPage. */
  mintUrl: string
  /**
   * DUAL during the Nov-Dec 2026 parallel run, ORACLE_ONLY from 1 Jan 2027.
   * The January cutover is a change to this value, not a release.
   */
  erpMode: ErpMode
  dualPeriod: { from: string; to: string }
  /**
   * The corporate logo shown alongside the SIPAY mark on the login screen.
   *
   * Configurable rather than imported, because the official artwork is a brand
   * asset that legal and corporate communications own, not something this repo
   * should carry a hand-drawn approximation of. Drop the real file at
   * public/brand/ptsi-logo.png and it appears; until then the login screen
   * renders the wordmark on its own and nothing looks broken.
   */
  companyLogoUrl: string
  companyName: string
}

const FALLBACK: AppConfig = {
  apiBaseUrl: '/api/v1',
  mintUrl: '',
  erpMode: 'DUAL',
  dualPeriod: { from: '2026-11-01', to: '2026-12-31' },
  companyLogoUrl: '/brand/ptsi-logo.png',
  companyName: 'PT Surveyor Indonesia',
}

let config: AppConfig = FALLBACK

export async function loadConfig(): Promise<AppConfig> {
  try {
    const res = await fetch('/config.json', { cache: 'no-store' })
    if (res.ok) {
      config = { ...FALLBACK, ...(await res.json()) }
    }
  } catch {
    // A missing or malformed config must not produce a blank page: the
    // fallback points at the same-origin API, which is the common case.
    console.warn('config.json tidak dapat dibaca, memakai nilai bawaan')
  }
  return config
}

export function getConfig(): AppConfig {
  return config
}
